import { useState, useEffect, useRef } from "react";

/**
 * Smart lazy-loading image with skeleton + fade-in on load.
 *
 * - `loading="lazy"` defers download until the image is near the viewport
 * - `decoding="async"` keeps the main thread free
 * - Skeleton shimmer shows while loading (matches our dark aesthetic)
 * - Image fades in smoothly when it decodes
 * - Falls back to a neutral grey block on error (no broken-image icon)
 *
 * Props:
 *   src       — image URL
 *   alt       — required for a11y
 *   aspect    — Tailwind aspect-ratio class (default: "aspect-[3/2]")
 *   className — applied to the wrapper
 *   imgClass  — applied to the <img> element specifically
 *   eager     — true to bypass lazy loading (use for above-the-fold hero images)
 *   rounded   — Tailwind rounded class (default: "rounded-md")
 */
export default function LazyImage({
  src,
  alt = "",
  aspect = "aspect-[3/2]",
  className = "",
  imgClass = "",
  eager = false,
  rounded = "rounded-md",
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const imgRef = useRef(null);

  // If the image is already cached and complete by the time React mounts,
  // the `onLoad` event won't fire — handle that synchronously.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <div
      className={`relative overflow-hidden bg-[#0a0a0a] ${aspect} ${rounded} ${className}`}
      data-testid="lazy-image"
    >
      {/* Skeleton shimmer — visible while loading */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          loaded || errored ? "opacity-0" : "opacity-100"
        }`}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-white/[0.06] to-white/[0.02]" />
        <div className="absolute inset-0 shimmer-anim" />
      </div>

      {!errored && src ? (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            loaded ? "opacity-100" : "opacity-0"
          } ${imgClass}`}
        />
      ) : null}

      {/* Error fallback — neutral block, no broken-image icon */}
      {errored && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/[0.03]">
          <div className="text-[10px] uppercase tracking-widest font-mono text-white/25">
            {alt || "Image unavailable"}
          </div>
        </div>
      )}

      <style>{`
        @keyframes vfd-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .shimmer-anim {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.04) 50%,
            transparent 100%
          );
          animation: vfd-shimmer 1.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
